import * as THREE from "three";

/**
 * A vertical gradient stored as an equirectangular texture.
 *
 * `DataTexture` leaves `flipY` off, and three's equirectangular lookup puts
 * `v = 1` straight up, so the last row of the buffer is the zenith and the
 * first row is the nadir. The rows are written in that order rather than
 * flipped afterwards, so the sky ends up overhead and the light lands on the
 * tops of the pieces.
 *
 * Only a handful of rows are needed — the gradient is smooth, and the sampler
 * interpolates between them — but the width has to be more than one texel or
 * the wrap seam shows up as a hard line.
 */
export function createGradientTexture(
  nadir: THREE.ColorRepresentation,
  horizon: THREE.ColorRepresentation,
  zenith: THREE.ColorRepresentation,
): THREE.DataTexture {
  const width = 4;
  const height = 64;
  const data = new Uint8Array(width * height * 4);
  const low = new THREE.Color(nadir);
  const middle = new THREE.Color(horizon);
  const high = new THREE.Color(zenith);
  const color = new THREE.Color();
  for (let y = 0; y < height; y += 1) {
    const t = y / (height - 1);
    if (t < 0.5) {
      color.copy(low).lerp(middle, t * 2);
    } else {
      color.copy(middle).lerp(high, (t - 0.5) * 2);
    }
    for (let x = 0; x < width; x += 1) {
      const offset = (y * width + x) * 4;
      data[offset] = Math.round(color.r * 255);
      data[offset + 1] = Math.round(color.g * 255);
      data[offset + 2] = Math.round(color.b * 255);
      data[offset + 3] = 255;
    }
  }
  const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}
